import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { Plan } from '../../types/plan';
import { PlanService } from '../../services/PlanService';
import { PlanAccessValidator } from '../../services/PlanAccessValidator';
import { CreditCard, MapPin, User, Lock, ArrowLeft } from 'lucide-react';
import AppHeader from '../AppHeader';
import InfoModal from '../InfoModal';
import { CHECKOUT_MESSAGES } from '../../constants/checkout';

interface CheckoutFormData {
  cardNumber: string;
  cardName: string;
  cardExpiry: string;
  cardCVV: string;
  cpfCnpj: string;
  phone: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface ResolveCheckoutIdentityResponse {
  success: boolean;
  checkoutIdentityId: string;
  isExistingUser: boolean;
  passwordSet: boolean;
  email: string;
  message?: string;
}

interface CheckCheckoutEmailExistsResponse {
  success: boolean;
  email: string;
  exists: boolean;
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof (window as Window & { fbq?: (...args: unknown[]) => void }).fbq === 'function') {
      (window as Window & { fbq?: (...args: unknown[]) => void }).fbq?.('track', 'InitiateCheckout');
    }
  }, []);
  
  const [plan, setPlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [hasBillingInfo, setHasBillingInfo] = useState(false);
  const [cardLast4Digits, setCardLast4Digits] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'pix'>('card');
  const [pixData, setPixData] = useState<{
    qrCode: string;
    pixCopyPaste: string;
    expirationDate: string;
    chargeId?: string;
    identityAssurance?: 'authenticated' | 'email-only';
    identityOutcome?: 'existing-user' | 'new-user-password-set' | 'new-user-password-pending';
    email?: string;
  } | null>(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount: number;
    type: 'percentage' | 'fixed';
  } | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning' as 'error' | 'warning' | 'info' | 'success',
    redirectTo: ''
  });
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutConfirmEmail, setCheckoutConfirmEmail] = useState('');
  const [checkoutIdentityId, setCheckoutIdentityId] = useState('');
  const [isIdentityResolved, setIsIdentityResolved] = useState(false);
  const [isResolvingIdentity, setIsResolvingIdentity] = useState(false);
  const [isCheckingCheckoutEmail, setIsCheckingCheckoutEmail] = useState(false);
  const [checkoutEmailExists, setCheckoutEmailExists] = useState<boolean | null>(null);
  const [setPasswordNow, setSetPasswordNow] = useState(false);
  const [checkoutPassword, setCheckoutPassword] = useState('');
  const [checkoutConfirmPassword, setCheckoutConfirmPassword] = useState('');
  const pixQrSectionRef = useRef<HTMLDivElement | null>(null);
  
  const [formData, setFormData] = useState<CheckoutFormData>({
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVV: '',
    cpfCnpj: '',
    phone: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      setIsIdentityResolved(true);
    }
  }, [isAuthenticated]);

  // Obter planId dos parâmetros da URL
  const searchParams = new URLSearchParams(location.search);
  const planId = searchParams.get('planId');

  useEffect(() => {
    const fetchPlan = async () => {
      if (!planId) {
        navigate('/planos');
        return;
      }

      // Usuário autenticado mas com perfil incompleto (ex: Google login novo)
      // deve completar o cadastro antes de prosseguir com o checkout
      if (user && user.role === 'login-not-completed') {
        navigate('/completar-perfil', {
          replace: true,
          state: { from: { pathname: location.pathname, search: location.search } },
        });
        return;
      }

      try {
        const planDoc = await getDoc(doc(db, 'plans', planId));
        if (!planDoc.exists()) {
          navigate('/planos');
          return;
        }

        const planData = PlanService.fromFirestore(planDoc.id, planDoc.data());
        
        // Validar se o usuário pode acessar este plano
        if (user && user.role) {
          const validation = PlanAccessValidator.canAccessPlan(user.role, planData.userType);
          
          if (!validation.canAccess) {
            setModalState({
              isOpen: true,
              title: validation.title || 'Acesso Negado',
              message: validation.message || '',
              variant: validation.variant || 'warning',
              redirectTo: validation.redirectTo || '/planos'
            });
            return;
          }
        }
        
        setPlan(planData);

        // Verificar se o usuário possui cartão salvo
        if (isAuthenticated && user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const creditCardToken = userData?.billingInfo?.creditCardToken;
            const last4 = userData?.billingInfo?.cardLast4Digits;
            
            // Verificar se tem billingInfo completa (com endereço)
            if (userData?.billingInfo?.address) {
              setHasBillingInfo(true);
            }
            
            if (creditCardToken) {
              setHasSavedCard(true);
              setCardLast4Digits(last4 || '');
              setCardExpiry(userData?.billingInfo?.cardExpiry || '');
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar plano:', error);
        navigate('/planos');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlan();
  }, [isAuthenticated, user, planId, navigate, location.pathname, location.search]);

  // Verificar status do pagamento PIX
  useEffect(() => {
    if (!pixData?.chargeId || paymentConfirmed) return;

    const checkPaymentStatus = async () => {
      try {
        const checkPaymentFunction = httpsCallable(functions, 'checkPaymentStatus');
        const result = await checkPaymentFunction({
          chargeId: pixData.chargeId,
          ...(!isAuthenticated && checkoutIdentityId ? { checkoutIdentityId } : {}),
        });
        const response = result.data as { 
          success: boolean; 
          status?: string;
          message?: string;
        };

        if (response.success && response.status === 'CONFIRMED') {
          setPaymentConfirmed(true);
          setIsCheckingPayment(false);
          
          // Aguardar 2 segundos antes de redirecionar
          setTimeout(() => {
            navigate('/pagamento/sucesso', { 
              state: { 
                chargeId: pixData.chargeId,
                message: 'Pagamento PIX confirmado com sucesso!',
                planName: plan?.name,
                identityAssurance: pixData.identityAssurance || (isAuthenticated ? 'authenticated' : 'email-only'),
                identityOutcome: pixData.identityOutcome || 'existing-user',
                checkoutEmail: pixData.email || checkoutEmail,
              } 
            });
          }, 2000);
        }
      } catch (error) {
        console.error('Erro ao verificar status do pagamento:', error);
      }
    };

    // Iniciar polling a cada 5 segundos
    setIsCheckingPayment(true);
    const interval = setInterval(checkPaymentStatus, 5000);
    
    // Verificar imediatamente
    checkPaymentStatus();

    // Limpar intervalo após 15 minutos (tempo máximo de espera)
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setIsCheckingPayment(false);
    }, 15 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [pixData, paymentConfirmed, navigate, plan, isAuthenticated, checkoutIdentityId, checkoutEmail]);

  useEffect(() => {
    if (!pixData || paymentConfirmed) return;

    const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobileViewport) return;

    // Quando o QR Code é gerado, leva o usuário até a seção para escanear sem precisar procurar na tela.
    const scrollToQrCode = () => {
      pixQrSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const timeoutId = window.setTimeout(scrollToQrCode, 100);
    return () => window.clearTimeout(timeoutId);
  }, [pixData, paymentConfirmed]);

  const handleInputChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Formatação de inputs
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // 16 dígitos + 3 espaços
  };

  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}`;
    }
    return cleaned;
  };

  const formatCpfCnpj = (value: string) => {
    const cleaned = value.replace(/\D/g, '');

    // CPF: 11 dígitos -> 000.000.000-00
    if (cleaned.length <= 11) {
      const cpf = cleaned.substring(0, 11);
      return cpf
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2');
    }

    // CNPJ: 14 dígitos -> 00.000.000/0000-00
    const cnpj = cleaned.substring(0, 14);
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
  };

  const isValidCpf = (cpf: string) => {
    if (!cpf || cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
      return false;
    }

    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += Number(cpf.charAt(i)) * (10 - i);
    }

    let firstCheckDigit = (sum * 10) % 11;
    if (firstCheckDigit === 10) {
      firstCheckDigit = 0;
    }

    if (firstCheckDigit !== Number(cpf.charAt(9))) {
      return false;
    }

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += Number(cpf.charAt(i)) * (11 - i);
    }

    let secondCheckDigit = (sum * 10) % 11;
    if (secondCheckDigit === 10) {
      secondCheckDigit = 0;
    }

    return secondCheckDigit === Number(cpf.charAt(10));
  };

  const isValidCnpj = (cnpj: string) => {
    if (!cnpj || cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
      return false;
    }

    const calculateCheckDigit = (base: string, weights: number[]) => {
      const total = base
        .split('')
        .reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);
      const remainder = total % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };

    const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const firstCheckDigit = calculateCheckDigit(cnpj.slice(0, 12), firstWeights);
    const secondCheckDigit = calculateCheckDigit(cnpj.slice(0, 12) + firstCheckDigit, secondWeights);

    return (
      firstCheckDigit === Number(cnpj.charAt(12)) &&
      secondCheckDigit === Number(cnpj.charAt(13))
    );
  };

  const isValidCpfOrCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
      return isValidCpf(digits);
    }
    if (digits.length === 14) {
      return isValidCnpj(digits);
    }
    return false;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 15);
  };

  const formatZipCode = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{5})(\d)/, '$1-$2').substring(0, 9);
  };

  const fetchAddressByZipCode = async (zipCode: string) => {
    const cleanedZip = zipCode.replace(/\D/g, '');
    
    // Verifica se o CEP tem 8 dígitos
    if (cleanedZip.length !== 8) {
      return;
    }

    setIsLoadingAddress(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedZip}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        setModalState({
          isOpen: true,
          title: 'CEP não encontrado',
          message: 'O CEP informado não foi encontrado. Verifique e tente novamente.',
          variant: 'warning',
          redirectTo: ''
        });
        return;
      }
      
      // Preencher automaticamente os campos
      setFormData(prev => ({
        ...prev,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state
      }));
      
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setModalState({
        isOpen: true,
        title: 'Erro ao buscar CEP',
        message: 'Não foi possível buscar o endereço. Preencha manualmente.',
        variant: 'warning',
        redirectTo: ''
      });
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleZipCodeChange = (value: string) => {
    const formatted = formatZipCode(value);
    handleInputChange('zipCode', formatted);
    
    // Buscar endereço quando o CEP estiver completo
    const cleanedZip = formatted.replace(/\D/g, '');
    if (cleanedZip.length === 8) {
      fetchAddressByZipCode(formatted);
    }
  };

  const applyCoupon = async () => {
    const trimmedCode = couponCode.trim();
    
    if (!trimmedCode) {
      return;
    }

    setIsApplyingCoupon(true);

    try {
      // Chamar função para validar cupom
      const validateCouponFunction = httpsCallable(functions, 'validateCoupon');
      const result = await validateCouponFunction({ 
        couponCode: trimmedCode,
        planId: plan?.id 
      });
      
      const response = result.data as { 
        success: boolean; 
        discount?: number;
        type?: 'percentage' | 'fixed';
        message?: string;
      };

      if (response.success && response.discount) {
        setAppliedCoupon({
          code: trimmedCode,
          discount: response.discount,
          type: response.type || 'percentage'
        });
        setModalState({
          isOpen: true,
          title: 'Cupom Aplicado!',
          message: `Cupom "${trimmedCode}" aplicado com sucesso.`,
          variant: 'success',
          redirectTo: ''
        });
      } else {
        setAppliedCoupon(null);
        setModalState({
          isOpen: true,
          title: 'Cupom Inválido',
          message: response.message || 'O cupom informado não é válido ou está expirado.',
          variant: 'warning',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao validar cupom:', error);
      setAppliedCoupon(null);
      setModalState({
        isOpen: true,
        title: 'Erro ao Validar Cupom',
        message: error?.message || 'Não foi possível validar o cupom. Tente novamente.',
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleResolveCheckoutIdentity = async () => {
    const normalizedEmail = checkoutEmail.trim().toLowerCase();
    if (!normalizedEmail || !plan?.id) {
      setModalState({
        isOpen: true,
        title: 'Dados Incompletos',
        message: 'Informe um email válido para continuar.',
        variant: 'warning',
        redirectTo: ''
      });
      return;
    }

    let emailExists = checkoutEmailExists;

    if (!isAuthenticated && emailExists === null) {
      setIsCheckingCheckoutEmail(true);
      try {
        const checkCheckoutEmailExistsFunction = httpsCallable(functions, 'checkCheckoutEmailExists');
        const checkResult = await checkCheckoutEmailExistsFunction({
          email: normalizedEmail,
        });

        const checkResponse = checkResult.data as CheckCheckoutEmailExistsResponse;
        if (!checkResponse.success) {
          throw new Error(CHECKOUT_MESSAGES.identity.checkEmailError);
        }

        emailExists = checkResponse.exists;
        setCheckoutEmailExists(checkResponse.exists);

        if (!checkResponse.exists) {
          setModalState({
            isOpen: true,
            title: CHECKOUT_MESSAGES.identity.title,
            message: CHECKOUT_MESSAGES.identity.newUserPrompt,
            variant: 'info',
            redirectTo: ''
          });
          return;
        }
      } catch (error: any) {
        setModalState({
          isOpen: true,
          title: 'Não foi possível continuar',
          message: error?.message || CHECKOUT_MESSAGES.identity.checkEmailError,
          variant: 'error',
          redirectTo: ''
        });
        return;
      } finally {
        setIsCheckingCheckoutEmail(false);
      }
    }

    const requiresEmailConfirmation = !isAuthenticated && emailExists === false;
    const isConfirmEmailMatch =
      checkoutConfirmEmail.trim().toLowerCase() === normalizedEmail;

    if (requiresEmailConfirmation && !isConfirmEmailMatch) {
      setModalState({
        isOpen: true,
        title: 'Dados Incompletos',
        message: CHECKOUT_MESSAGES.identity.confirmEmailMismatch,
        variant: 'warning',
        redirectTo: ''
      });
      return;
    }

    if (requiresEmailConfirmation && setPasswordNow) {
      if (checkoutPassword.length < 6) {
        setModalState({
          isOpen: true,
          title: 'Senha inválida',
          message: 'A senha deve ter ao menos 6 caracteres.',
          variant: 'warning',
          redirectTo: ''
        });
        return;
      }

      if (checkoutPassword !== checkoutConfirmPassword) {
        setModalState({
          isOpen: true,
          title: 'Senha inválida',
          message: CHECKOUT_MESSAGES.identity.passwordMismatch,
          variant: 'warning',
          redirectTo: ''
        });
        return;
      }
    }

    setIsResolvingIdentity(true);
    try {
      const resolveCheckoutIdentityFunction = httpsCallable(functions, 'resolveCheckoutIdentity');
      const result = await resolveCheckoutIdentityFunction({
        email: normalizedEmail,
        planId: plan.id,
        setPasswordNow: requiresEmailConfirmation && setPasswordNow,
        ...(requiresEmailConfirmation && setPasswordNow ? { password: checkoutPassword } : {}),
      });

      const response = result.data as ResolveCheckoutIdentityResponse;
      if (!response.success || !response.checkoutIdentityId) {
        throw new Error(response.message || CHECKOUT_MESSAGES.identity.resolveError);
      }

      setCheckoutIdentityId(response.checkoutIdentityId);
      setCheckoutEmail(response.email || normalizedEmail);
      setCheckoutEmailExists(response.isExistingUser);
      setIsIdentityResolved(true);

      setModalState({
        isOpen: true,
        title: 'Pronto',
        message: response.message || CHECKOUT_MESSAGES.identity.resolveSuccess,
        variant: 'success',
        redirectTo: ''
      });
    } catch (error: any) {
      setIsIdentityResolved(false);
      setCheckoutIdentityId('');
      setModalState({
        isOpen: true,
        title: 'Não foi possível continuar',
        message: error?.message || CHECKOUT_MESSAGES.identity.resolveError,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsResolvingIdentity(false);
    }
  };

  const handleCouponBlur = () => {
    if (couponCode.trim() && !appliedCoupon) {
      applyCoupon();
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const isEmailConfirmationRequired = !isAuthenticated && checkoutEmailExists === false;
  const isEmailConfirmed =
    checkoutConfirmEmail.trim().toLowerCase() === checkoutEmail.trim().toLowerCase();

  const calculateFinalValue = () => {
    if (!plan || !appliedCoupon) return plan?.value || 0;

    if (appliedCoupon.type === 'percentage') {
      return plan.value * (1 - appliedCoupon.discount / 100);
    } else {
      return Math.max(0, plan.value - appliedCoupon.discount);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      if (!isAuthenticated && !isIdentityResolved) {
        setModalState({
          isOpen: true,
          title: 'Identificação necessária',
          message: 'Informe seu email e continue para liberar o pagamento.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      // Validar campos obrigatórios
      if (!formData.cardNumber || !formData.cardName || !formData.cardExpiry || !formData.cardCVV) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: 'Por favor, preencha todos os dados do cartão.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      if (!formData.cpfCnpj || !formData.phone) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: 'Por favor, preencha CPF/CNPJ e telefone.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      if (!isValidCpfOrCnpj(formData.cpfCnpj)) {
        setModalState({
          isOpen: true,
          title: 'Documento inválido',
          message: CHECKOUT_MESSAGES.validation.invalidDocument,
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      if (!formData.zipCode || !formData.street || !formData.number || 
          !formData.neighborhood || !formData.city || !formData.state) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: 'Por favor, preencha o endereço completo.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      // Chamar função de pagamento (tokenização feita no backend)
      const processPaymentFunction = httpsCallable(functions, 'processPayment');
      
      const paymentData = {
        planId: plan.id,
        ...(!isAuthenticated && { checkoutIdentityId }),
        cardNumber: formData.cardNumber,
        cardName: formData.cardName,
        cardExpiry: formData.cardExpiry,
        cardCVV: formData.cardCVV,
        cpfCnpj: formData.cpfCnpj,
        phone: formData.phone,
        zipCode: formData.zipCode,
        street: formData.street,
        number: formData.number,
        complement: formData.complement,
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
      };

      const result = await processPaymentFunction(paymentData);
      const response = result.data as { 
        success: boolean; 
        subscriptionId?: string; 
        chargeId?: string;
        invoiceUrl?: string;
        message?: string;
      };

      if (response.success) {
        // Redirecionar para página de sucesso
        navigate('/pagamento/sucesso', { 
          state: { 
            subscriptionId: response.subscriptionId,
            chargeId: response.chargeId,
            invoiceUrl: response.invoiceUrl,
            message: response.message,
            planName: plan.name,
            identityAssurance: (response as any).identityAssurance,
            identityOutcome: (response as any).identityOutcome,
            checkoutEmail,
          } 
        });
      } else {
        setModalState({
          isOpen: true,
          title: 'Erro no Pagamento',
          message: response.message || 'Não foi possível processar o pagamento. Verifique os dados e tente novamente.',
          variant: 'error',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao processar pagamento:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (error?.code === 'invalid-argument') {
        errorMessage = error?.message || 'Dados inválidos. Verifique as informações e tente novamente.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setModalState({
        isOpen: true,
        title: 'Erro no Pagamento',
        message: errorMessage,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayWithSavedCard = async () => {
    setIsProcessing(true);

    try {
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      if (!isAuthenticated && !isIdentityResolved) {
        setModalState({
          isOpen: true,
          title: 'Identificação necessária',
          message: 'Informe seu email e continue para liberar o pagamento.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      // Chamar função para processar com cartão salvo
      const processPaymentWithSavedCardFunction = httpsCallable(functions, 'processPaymentWithSavedCard');
      
      const paymentData = {
        planId: plan.id,
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
      };

      const result = await processPaymentWithSavedCardFunction(paymentData);
      const response = result.data as { 
        success: boolean; 
        subscriptionId?: string; 
        chargeId?: string;
        invoiceUrl?: string;
        message?: string;
      };

      if (response.success) {
        // Redirecionar para página de sucesso
        navigate('/pagamento/sucesso', { 
          state: { 
            subscriptionId: response.subscriptionId,
            chargeId: response.chargeId,
            invoiceUrl: response.invoiceUrl,
            message: response.message,
            planName: plan.name
          } 
        });
      } else {
        setModalState({
          isOpen: true,
          title: 'Erro no Pagamento',
          message: response.message || 'Não foi possível processar o pagamento com o cartão salvo.',
          variant: 'error',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao processar pagamento com cartão salvo:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (error?.code === 'failed-precondition') {
        errorMessage = error?.message || 'Cartão salvo inválido. Use outro cartão.';
        setUseNewCard(true);
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setModalState({
        isOpen: true,
        title: 'Erro no Pagamento',
        message: errorMessage,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePixPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      // Validar CPF/CNPJ
      if (!formData.cpfCnpj || !isValidCpfOrCnpj(formData.cpfCnpj)) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: CHECKOUT_MESSAGES.validation.invalidDocument,
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      // Validar endereço obrigatório se não tiver billingInfo salva
      if (!hasBillingInfo) {
        if (!formData.zipCode || !formData.street || !formData.number || 
            !formData.neighborhood || !formData.city || !formData.state) {
          setModalState({
            isOpen: true,
            title: 'Dados Incompletos',
            message: 'Por favor, preencha o endereço completo. Essas informações são obrigatórias para cadastro.',
            variant: 'warning',
            redirectTo: ''
          });
          setIsProcessing(false);
          return;
        }
      }

      // Chamar função de pagamento PIX
      const processPixPaymentFunction = httpsCallable(functions, 'processPixPayment');
      
      const paymentData = {
        planId: plan.id,
        ...(!isAuthenticated && { checkoutIdentityId }),
        cpfCnpj: formData.cpfCnpj,
        phone: formData.phone || '',
        // Incluir dados de endereço se não tiver billingInfo salva
        ...(!hasBillingInfo && {
          zipCode: formData.zipCode,
          street: formData.street,
          number: formData.number,
          complement: formData.complement,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state,
        }),
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
      };

      const result = await processPixPaymentFunction(paymentData);
      const response = result.data as { 
        success: boolean; 
        qrCode?: string;
        pixCopyPaste?: string;
        expirationDate?: string;
        chargeId?: string;
        message?: string;
        identityAssurance?: 'authenticated' | 'email-only';
        identityOutcome?: 'existing-user' | 'new-user-password-set' | 'new-user-password-pending';
        email?: string;
      };

      if (response.success) {
        if (response.qrCode && response.pixCopyPaste) {
          // Armazenar dados do PIX para exibir
          setPixData({
            qrCode: response.qrCode,
            pixCopyPaste: response.pixCopyPaste,
            expirationDate: response.expirationDate || '',
            chargeId: response.chargeId,
            identityAssurance: response.identityAssurance,
            identityOutcome: response.identityOutcome,
            email: response.email,
          });
        } else {
          setPaymentConfirmed(true);
          setIsCheckingPayment(false);
          
          navigate('/pagamento/sucesso', { 
            state: { 
              chargeId: response.chargeId,
              message: 'Pagamento PIX confirmado com sucesso!',
              planName: plan?.name,
              identityAssurance: response.identityAssurance,
              identityOutcome: response.identityOutcome,
              checkoutEmail: response.email || checkoutEmail,
            } 
          });
        }
      } else {
        setModalState({
          isOpen: true,
          title: 'Erro no Pagamento',
          message: response.message || 'Não foi possível gerar o pagamento PIX. Tente novamente.',
          variant: 'error',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao processar pagamento PIX:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (error?.code === 'invalid-argument') {
        errorMessage = error?.message || 'Dados inválidos. Verifique as informações e tente novamente.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setModalState({
        isOpen: true,
        title: 'Erro no Pagamento',
        message: errorMessage,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C528A] mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 sm:pt-24 sm:pb-12 lg:py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 sm:mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulário de Checkout */}
          <div className="lg:col-span-2 order-1 lg:order-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <>
                  <h1 className="text-3xl font-bold text-gray-900 mb-8">{CHECKOUT_MESSAGES.pageTitle}</h1>

              {!plan.isRecurring && !pixData && (
                <div className="mb-8 lg:hidden border border-gray-200 rounded-xl p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cupom de Desconto
                  </label>
                  {!appliedCoupon ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onBlur={handleCouponBlur}
                        placeholder="Digite o cupom"
                        disabled={isApplyingCoupon}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400 text-sm disabled:bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={isApplyingCoupon || !couponCode.trim()}
                        className="px-4 py-2 bg-[#5C528A] text-white rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                      >
                        {isApplyingCoupon ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          'Aplicar'
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-green-900">{appliedCoupon?.code}</p>
                            <p className="text-xs text-green-700">
                              {appliedCoupon?.type === 'percentage'
                                ? `${appliedCoupon.discount}% de desconto`
                                : `${PlanService.formatCurrency(appliedCoupon?.discount ?? 0)} de desconto`
                              }
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          className="text-green-700 hover:text-green-900"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Seleção de Método de Pagamento - Apenas para planos não recorrentes */}
              {plan && !plan.isRecurring && !pixData && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Método de Pagamento</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        paymentMethod === 'card'
                          ? 'border-[#5C528A] bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <CreditCard className={`w-8 h-8 mx-auto mb-2 ${
                        paymentMethod === 'card' ? 'text-[#5C528A]' : 'text-gray-600'
                      }`} />
                      <p className={`font-semibold ${
                        paymentMethod === 'card' ? 'text-[#5C528A]' : 'text-gray-700'
                      }`}>
                        Cartão de Crédito
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('pix')}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        paymentMethod === 'pix'
                          ? 'border-[#5C528A] bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <svg
                        className={`w-8 h-8 mx-auto mb-2 ${
                          paymentMethod === 'pix' ? 'text-[#5C528A]' : 'text-gray-600'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.82 8 12 11.82 4.18 8 12 4.18zM4 10.18l7 3.5v6.14l-7-3.5v-6.14zm16 0v6.14l-7 3.5v-6.14l7-3.5z"/>
                      </svg>
                      <p className={`font-semibold ${
                        paymentMethod === 'pix' ? 'text-[#5C528A]' : 'text-gray-700'
                      }`}>
                        PIX
                      </p>
                    </button>
                  </div>
                </div>
              )}

              {/* Exibir QR Code do PIX se gerado */}
              {pixData && (
                <div className="space-y-6">
                  {/* Indicador de pagamento confirmado */}
                  {paymentConfirmed && (
                    <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-6 text-center animate-pulse">
                      <div className="flex justify-center mb-4">
                        <div className="bg-green-500 rounded-full p-3">
                          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-green-800 mb-2">
                        Pagamento Confirmado!
                      </h2>
                      <p className="text-green-700">
                        Redirecionando você em instantes...
                      </p>
                    </div>
                  )}

                  {/* Indicador de verificação de pagamento */}
                  {!paymentConfirmed && isCheckingPayment && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <p className="text-sm text-blue-800">
                          Aguardando confirmação do pagamento...
                        </p>
                      </div>
                    </div>
                  )}

                  <div ref={pixQrSectionRef} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-4 sm:p-8 text-center overflow-x-hidden">
                    <div className="bg-white rounded-xl p-3 sm:p-6 inline-block mb-6 max-w-full">
                      <img 
                        src={`data:image/png;base64,${pixData.qrCode}`} 
                        alt="QR Code PIX" 
                        className="w-52 h-52 sm:w-64 sm:h-64 max-w-full mx-auto"
                      />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Escaneie o QR Code para pagar
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Use o aplicativo do seu banco para escanear o código acima
                    </p>

                    {pixData.expirationDate && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-yellow-800">
                          ⏰ Este código expira em: <strong>{new Date(pixData.expirationDate).toLocaleString('pt-BR')}</strong>
                        </p>
                      </div>
                    )}

                    <div className="bg-white rounded-lg p-4 mb-6 max-w-full overflow-x-hidden">
                      <p className="text-sm text-gray-600 mb-2">Ou copie o código PIX:</p>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-full">
                        <input
                          type="text"
                          value={pixData.pixCopyPaste}
                          readOnly
                          className="flex-1 min-w-0 w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(pixData.pixCopyPaste);
                            setModalState({
                              isOpen: true,
                              title: 'Código Copiado!',
                              message: 'O código PIX foi copiado para a área de transferência.',
                              variant: 'success',
                              redirectTo: ''
                            });
                          }}
                          className="w-full sm:w-auto px-4 py-2 bg-[#5C528A] text-white rounded-lg hover:bg-[#4A4070] transition-colors whitespace-nowrap"
                        >
                          Copiar
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3 text-left">
                      <h3 className="font-semibold text-gray-900">Como pagar:</h3>
                      <ol className="space-y-2 text-sm text-gray-600">
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">1</span>
                          <span>Abra o aplicativo do seu banco</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">2</span>
                          <span>Escolha pagar com PIX</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">3</span>
                          <span>Escaneie o QR Code ou cole o código</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">4</span>
                          <span>Confirme o pagamento</span>
                        </li>
                      </ol>
                    </div>

                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        💡 Após confirmar o pagamento, seu plano será ativado automaticamente em alguns instantes.
                      </p>
                    </div>

                    <button
                      onClick={() => navigate('/painel')}
                      className="mt-6 w-full bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                    >
                      Ir para o Painel
                    </button>
                  </div>
                </div>
              )}

              {/* Formulário de Pagamento - Ocultar se PIX foi gerado */}
              {!pixData && (
                <>
                  {/* Formulário PIX */}
                  {paymentMethod === 'pix' && (
                    <>
                    <form id="pix-form" onSubmit={handlePixPayment} className="space-y-6">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                        <div className="flex items-start gap-3 mb-4">
                          <svg className="w-6 h-6 text-[#5C528A] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.82 8 12 11.82 4.18 8 12 4.18zM4 10.18l7 3.5v6.14l-7-3.5v-6.14zm16 0v6.14l-7 3.5v-6.14l7-3.5z"/>
                          </svg>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">Pagamento via PIX</h3>
                            <p className="text-sm text-gray-600">
                              Após finalizar, você receberá um QR Code para realizar o pagamento instantaneamente.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <User className="w-5 h-5" />
                          Dados para Pagamento
                        </h2>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              CPF ou CNPJ *
                            </label>
                            <input
                              type="text"
                              required
                              value={formData.cpfCnpj}
                              onChange={(e) => handleInputChange('cpfCnpj', formatCpfCnpj(e.target.value))}
                              placeholder="Digite seu CPF ou CNPJ"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Telefone (opcional)
                            </label>
                            <input
                              type="text"
                              value={formData.phone}
                              onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                              placeholder="(00) 00000-0000"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Endereço obrigatório se não tiver billingInfo */}
                      {!hasBillingInfo && (
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <MapPin className="w-5 h-5" />
                            Endereço
                          </h2>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                CEP *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  required
                                  value={formData.zipCode}
                                  onChange={(e) => handleZipCodeChange(e.target.value)}
                                  placeholder="00000-000"
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                                />
                                {isLoadingAddress && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#5C528A]"></div>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Digite o CEP para preencher o endereço automaticamente
                              </p>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rua *
                              </label>
                              <input
                                type="text"
                                required
                                value={formData.street}
                                onChange={(e) => handleInputChange('street', e.target.value)}
                                placeholder="Ex: Rua das Flores"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Número *
                              </label>
                              <input
                                type="text"
                                required
                                value={formData.number}
                                onChange={(e) => handleInputChange('number', e.target.value)}
                                placeholder="Nº do imóvel"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Complemento
                              </label>
                              <input
                                type="text"
                                value={formData.complement}
                                onChange={(e) => handleInputChange('complement', e.target.value)}
                                placeholder="Apto, Bloco, etc"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Bairro *
                              </label>
                              <input
                                type="text"
                                required
                                value={formData.neighborhood}
                                onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                                placeholder="Ex: Centro"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Cidade *
                              </label>
                              <input
                                type="text"
                                required
                                value={formData.city}
                                onChange={(e) => handleInputChange('city', e.target.value)}
                                placeholder="Ex: São Paulo"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Estado *
                              </label>
                              <select
                                required
                                value={formData.state}
                                onChange={(e) => handleInputChange('state', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
                              >
                                <option value="">Selecione</option>
                                <option value="AC">Acre</option>
                                <option value="AL">Alagoas</option>
                                <option value="AP">Amapá</option>
                                <option value="AM">Amazonas</option>
                                <option value="BA">Bahia</option>
                                <option value="CE">Ceará</option>
                                <option value="DF">Distrito Federal</option>
                                <option value="ES">Espírito Santo</option>
                                <option value="GO">Goiás</option>
                                <option value="MA">Maranhão</option>
                                <option value="MT">Mato Grosso</option>
                                <option value="MS">Mato Grosso do Sul</option>
                                <option value="MG">Minas Gerais</option>
                                <option value="PA">Pará</option>
                                <option value="PB">Paraíba</option>
                                <option value="PR">Paraná</option>
                                <option value="PE">Pernambuco</option>
                                <option value="PI">Piauí</option>
                                <option value="RJ">Rio de Janeiro</option>
                                <option value="RN">Rio Grande do Norte</option>
                                <option value="RS">Rio Grande do Sul</option>
                                <option value="RO">Rondônia</option>
                                <option value="RR">Roraima</option>
                                <option value="SC">Santa Catarina</option>
                                <option value="SP">São Paulo</option>
                                <option value="SE">Sergipe</option>
                                <option value="TO">Tocantins</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {!isAuthenticated && !pixData && (
                        <div className="rounded-2xl border border-[#D8D1F0] bg-gradient-to-br from-[#F7F4FF] to-white p-5 sm:p-6 space-y-4">
                          <h2 className="text-lg font-semibold text-gray-900">{CHECKOUT_MESSAGES.identity.title}</h2>
                          <p className="text-sm text-gray-600">{CHECKOUT_MESSAGES.identity.description}</p>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.emailLabel}</label>
                            <input
                              type="email"
                              value={checkoutEmail}
                              onChange={(e) => {
                                setCheckoutEmail(e.target.value);
                                setCheckoutConfirmEmail('');
                                setCheckoutEmailExists(null);
                                setCheckoutIdentityId('');
                                setIsIdentityResolved(false);
                              }}
                              placeholder={CHECKOUT_MESSAGES.identity.emailPlaceholder}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                            />
                          </div>

                          {isEmailConfirmationRequired && (
                            <>
                              <p className="text-sm text-[#5C528A]">{CHECKOUT_MESSAGES.identity.newUserPrompt}</p>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.confirmEmailLabel}</label>
                                <input
                                  type="email"
                                  value={checkoutConfirmEmail}
                                  onChange={(e) => setCheckoutConfirmEmail(e.target.value)}
                                  placeholder={CHECKOUT_MESSAGES.identity.confirmEmailPlaceholder}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                                />
                              </div>
                            </>
                          )}

                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={setPasswordNow}
                              onChange={(e) => setSetPasswordNow(e.target.checked)}
                              className="h-4 w-4"
                            />
                            {CHECKOUT_MESSAGES.identity.passwordSwitch}
                          </label>

                          {setPasswordNow && (
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.passwordLabel}</label>
                                <input
                                  type="password"
                                  value={checkoutPassword}
                                  onChange={(e) => setCheckoutPassword(e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.confirmPasswordLabel}</label>
                                <input
                                  type="password"
                                  value={checkoutConfirmPassword}
                                  onChange={(e) => setCheckoutConfirmPassword(e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
                                />
                              </div>
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={handleResolveCheckoutIdentity}
                            disabled={isResolvingIdentity || isCheckingCheckoutEmail || (isEmailConfirmationRequired && !isEmailConfirmed)}
                            className="w-full sm:w-auto bg-[#5C528A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait"
                          >
                            {isResolvingIdentity
                              ? CHECKOUT_MESSAGES.identity.validatingButton
                              : (isCheckingCheckoutEmail
                                ? CHECKOUT_MESSAGES.identity.checkingEmailButton
                                : CHECKOUT_MESSAGES.identity.continueButton)}
                          </button>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isProcessing || (!isAuthenticated && !isIdentityResolved)}
                        className="w-full bg-[#5C528A] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Gerando PIX...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.82 8 12 11.82 4.18 8 12 4.18zM4 10.18l7 3.5v6.14l-7-3.5v-6.14zm16 0v6.14l-7 3.5v-6.14l7-3.5z"/>
                            </svg>
                            Gerar QR Code PIX
                          </>
                        )}
                      </button>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-sm text-gray-900 mb-2">Por que PIX?</h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                          <li>• Pagamento instantâneo e seguro</li>
                          <li>• Sem taxas adicionais</li>
                          <li>• Confirmação em tempo real</li>
                          <li>• Acesso imediato após confirmação</li>
                        </ul>
                      </div>
                    </form>

                    </>
                  )}

                  {/* Opção de Cartão Salvo */}
                  {paymentMethod === 'card' && hasSavedCard && !useNewCard && (
                <div className="mb-8">
                  <div className="border-2 border-[#5C528A] rounded-xl p-6 bg-purple-50">
                    <div className="flex items-start gap-4">
                      <CreditCard className="w-8 h-8 text-[#5C528A] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Cartão Salvo
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Usar cartão final •••• {cardLast4Digits}
                        </p>
                        <p className="text-gray-600 mb-4">
                          Vencimento {cardExpiry}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            type="button"
                  
                            onClick={handlePayWithSavedCard}
                            disabled={isProcessing}
                            className="w-full sm:w-auto bg-[#5C528A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                          >
                            {isProcessing ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Processando...
                              </>
                            ) : (
                              <>
                                <Lock className="w-5 h-5" />
                                Finalizar Compra
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setUseNewCard(true)}
                            className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                          >
                            Usar outro cartão
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mostrar formulário completo se não tiver cartão salvo ou escolher usar novo */}
              {paymentMethod === 'card' && (!hasSavedCard || useNewCard) && (
                <>
                  {hasSavedCard && useNewCard && (
                    <div className="mb-6">
                      <button
                        type="button"
                        onClick={() => setUseNewCard(false)}
                        className="text-[#5C528A] hover:text-[#4A4070] font-medium flex items-center gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Voltar para cartão salvo
                      </button>
                    </div>
                  )}

              <form id="card-form" onSubmit={handleSubmit} className="space-y-8">
                {/* Dados do Cartão */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Dados do Cartão
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número do Cartão *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardNumber}
                        onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
                        placeholder="Digite os 16 dígitos do cartão"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome no Cartão *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardName}
                        onChange={(e) => handleInputChange('cardName', e.target.value.toUpperCase())}
                        placeholder="Ex: MARIA DA SILVA"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Validade *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardExpiry}
                        onChange={(e) => handleInputChange('cardExpiry', formatExpiry(e.target.value))}
                        placeholder="MM/AA"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVV *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardCVV}
                        onChange={(e) => handleInputChange('cardCVV', e.target.value.replace(/\D/g, '').substring(0, 4))}
                        placeholder="Código de segurança"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Dados Pessoais */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Dados Pessoais
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CPF ou CNPJ *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cpfCnpj}
                        onChange={(e) => handleInputChange('cpfCnpj', formatCpfCnpj(e.target.value))}
                        placeholder="Digite seu CPF ou CNPJ"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                        placeholder="(00) 00000-0000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Endereço de Cobrança */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Endereço de Cobrança
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CEP *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          value={formData.zipCode}
                          onChange={(e) => handleZipCodeChange(e.target.value)}
                          placeholder="00000-000"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                        />
                        {isLoadingAddress && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#5C528A]"></div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Digite o CEP para preencher o endereço automaticamente
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rua *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.street}
                        onChange={(e) => handleInputChange('street', e.target.value)}
                        placeholder="Ex: Rua das Flores"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.number}
                        onChange={(e) => handleInputChange('number', e.target.value)}
                        placeholder="Nº do imóvel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Complemento
                      </label>
                      <input
                        type="text"
                        value={formData.complement}
                        onChange={(e) => handleInputChange('complement', e.target.value)}
                        placeholder="Apto, Bloco, etc"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bairro *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.neighborhood}
                        onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                        placeholder="Ex: Centro"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cidade *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="Ex: São Paulo"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estado *
                      </label>
                      <select
                        required
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
                      >
                        <option value="">Selecione</option>
                        <option value="AC">Acre</option>
                        <option value="AL">Alagoas</option>
                        <option value="AP">Amapá</option>
                        <option value="AM">Amazonas</option>
                        <option value="BA">Bahia</option>
                        <option value="CE">Ceará</option>
                        <option value="DF">Distrito Federal</option>
                        <option value="ES">Espírito Santo</option>
                        <option value="GO">Goiás</option>
                        <option value="MA">Maranhão</option>
                        <option value="MT">Mato Grosso</option>
                        <option value="MS">Mato Grosso do Sul</option>
                        <option value="MG">Minas Gerais</option>
                        <option value="PA">Pará</option>
                        <option value="PB">Paraíba</option>
                        <option value="PR">Paraná</option>
                        <option value="PE">Pernambuco</option>
                        <option value="PI">Piauí</option>
                        <option value="RJ">Rio de Janeiro</option>
                        <option value="RN">Rio Grande do Norte</option>
                        <option value="RS">Rio Grande do Sul</option>
                        <option value="RO">Rondônia</option>
                        <option value="RR">Roraima</option>
                        <option value="SC">Santa Catarina</option>
                        <option value="SP">São Paulo</option>
                        <option value="SE">Sergipe</option>
                        <option value="TO">Tocantins</option>
                      </select>
                    </div>
                  </div>
                </div>

                {!isAuthenticated && !pixData && (
                  <div className="rounded-2xl border border-[#D8D1F0] bg-gradient-to-br from-[#F7F4FF] to-white p-5 sm:p-6 space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900">{CHECKOUT_MESSAGES.identity.title}</h2>
                    <p className="text-sm text-gray-600">{CHECKOUT_MESSAGES.identity.description}</p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.emailLabel}</label>
                      <input
                        type="email"
                        value={checkoutEmail}
                        onChange={(e) => {
                          setCheckoutEmail(e.target.value);
                          setCheckoutConfirmEmail('');
                          setCheckoutEmailExists(null);
                          setCheckoutIdentityId('');
                          setIsIdentityResolved(false);
                        }}
                        placeholder={CHECKOUT_MESSAGES.identity.emailPlaceholder}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>

                    {isEmailConfirmationRequired && (
                      <>
                        <p className="text-sm text-[#5C528A]">{CHECKOUT_MESSAGES.identity.newUserPrompt}</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.confirmEmailLabel}</label>
                          <input
                            type="email"
                            value={checkoutConfirmEmail}
                            onChange={(e) => setCheckoutConfirmEmail(e.target.value)}
                            placeholder={CHECKOUT_MESSAGES.identity.confirmEmailPlaceholder}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                          />
                        </div>
                      </>
                    )}

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={setPasswordNow}
                        onChange={(e) => setSetPasswordNow(e.target.checked)}
                        className="h-4 w-4"
                      />
                      {CHECKOUT_MESSAGES.identity.passwordSwitch}
                    </label>

                    {setPasswordNow && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.passwordLabel}</label>
                          <input
                            type="password"
                            value={checkoutPassword}
                            onChange={(e) => setCheckoutPassword(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{CHECKOUT_MESSAGES.identity.confirmPasswordLabel}</label>
                          <input
                            type="password"
                            value={checkoutConfirmPassword}
                            onChange={(e) => setCheckoutConfirmPassword(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleResolveCheckoutIdentity}
                      disabled={isResolvingIdentity || isCheckingCheckoutEmail || (isEmailConfirmationRequired && !isEmailConfirmed)}
                      className="w-full sm:w-auto bg-[#5C528A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait"
                    >
                      {isResolvingIdentity
                        ? CHECKOUT_MESSAGES.identity.validatingButton
                        : (isCheckingCheckoutEmail
                          ? CHECKOUT_MESSAGES.identity.checkingEmailButton
                          : CHECKOUT_MESSAGES.identity.continueButton)}
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  form="card-form"
                  disabled={isProcessing || (!isAuthenticated && !isIdentityResolved)}
                  className="w-full bg-[#5C528A] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Finalizar Compra
                    </>
                  )}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Pagamento seguro e criptografado
                </p>
              </form>

              </>
              )}
              </>
              )}
              </>
            </div>
          </div>

          {/* Resumo do Pedido */}
          <div className="lg:col-span-1 order-2 lg:order-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:sticky lg:top-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Resumo do Pedido</h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Período:</span>
                    <span className="font-medium">
                      {PlanService.formatPeriod(plan.quantity, plan.billingType, plan.isRecurring)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Tipo:</span>
                    <span className="font-medium">
                      {plan.isRecurring ? 'Assinatura Recorrente' : 'Pagamento Único'}
                    </span>
                  </div>
                  {!plan.isRecurring && !pixData && (
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Método:</span>
                      <span className="font-medium">
                        {paymentMethod === 'card' ? 'Cartão de Crédito' : 'PIX'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Campo de Cupom - Apenas para planos não-recorrentes */}
                {!plan.isRecurring && !pixData && (
                  <div className="hidden lg:block border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cupom de Desconto
                    </label>
                    {!appliedCoupon ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          onBlur={handleCouponBlur}
                          placeholder="Digite o cupom"
                          disabled={isApplyingCoupon}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400 text-sm disabled:bg-gray-100"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={isApplyingCoupon || !couponCode.trim()}
                          className="px-4 py-2 bg-[#5C528A] text-white rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                        >
                          {isApplyingCoupon ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            'Aplicar'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <div>
                              <p className="text-sm font-semibold text-green-900">{appliedCoupon?.code}</p>
                              <p className="text-xs text-green-700">
                                {appliedCoupon?.type === 'percentage'
                                  ? `${appliedCoupon.discount}% de desconto`
                                  : `${PlanService.formatCurrency(appliedCoupon?.discount ?? 0)} de desconto`
                                }
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeCoupon}
                            className="text-green-700 hover:text-green-900"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                {appliedCoupon && (
                  <>
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Subtotal:</span>
                      <span>{PlanService.formatCurrency(plan.value)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600 mb-3">
                      <span>Desconto:</span>
                      <span>
                        -{PlanService.formatCurrency(plan.value - calculateFinalValue())}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total:</span>
                  <span className="text-3xl font-bold text-[#5C528A]">
                    {PlanService.formatCurrency(appliedCoupon ? calculateFinalValue() : plan.value)}
                  </span>
                </div>
                {plan.isRecurring && (
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    Cobrado mensalmente
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-gray-900 mb-2">Informações Importantes:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Pagamento processado de forma segura</li>
                  <li>• Você receberá um email de confirmação</li>
                  {plan.isRecurring && (
                    <li>• Cancele a qualquer momento pelo painel</li>
                  )}
                  {!plan.isRecurring && paymentMethod === 'pix' && (
                    <li>• Pagamento confirmado instantaneamente</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <InfoModal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          if (modalState.redirectTo) {
            navigate(modalState.redirectTo);
          }
        }}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        buttonText="Entendi"
      />
    </div>
  );
};

export default CheckoutPage;
